// components/leads/LostLeadsPanel.js
// Pannello dedicato per gestire i lead in stato "Perso":
// - Multi-select
// - Recupero in blocco -> stato "nuovo"
// - Export CSV con preventivi collegati

import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { RotateCcw, Download, Loader2, X, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { leadApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const LostLeadsPanel = ({ leads = [], onRecoveryDone, canManage = true }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [exporting, setExporting] = useState(false);

  const lostLeads = useMemo(
    () => leads.filter((l) => l.status === 'perso'),
    [leads]
  );

  const allSelected = lostLeads.length > 0 && selectedIds.size === lostLeads.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (leadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(lostLeads.map((l) => l.id)));
    }
  };

  const handleBulkRecover = async () => {
    if (selectedIds.size === 0) return;
    setRecovering(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await leadApi.bulkRecover(ids, bulkNote.trim() || null);
      const { recovered_count, skipped_count } = res.data || {};
      if (recovered_count > 0) {
        toast.success(
          `Recuperati ${recovered_count} lead${skipped_count ? ` (${skipped_count} saltati)` : ''}`
        );
      } else {
        toast.warning('Nessun lead recuperato');
      }
      setSelectedIds(new Set());
      setBulkNote('');
      setShowConfirmModal(false);
      if (typeof onRecoveryDone === 'function') await onRecoveryDone();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel recupero in blocco');
    } finally {
      setRecovering(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await leadApi.exportLostCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.download = `lead_persi_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV avviato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'export CSV');
    } finally {
      setExporting(false);
    }
  };

  if (lostLeads.length === 0) {
    return (
      <div
        className="bg-white rounded-xl border border-[#EDEDED] p-8 text-center text-[#737373]"
        data-testid="lost-leads-empty"
      >
        <UserIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Nessun lead in stato "Perso"</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={handleExportCsv}
          disabled={exporting}
          data-testid="export-lost-csv-btn-empty"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {exporting ? 'Esporto...' : 'Esporta CSV (vuoto)'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="lost-leads-panel">
      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-[#EDEDED] p-3 flex flex-wrap items-center gap-2 shadow-sm">
        <div className="flex items-center gap-2 mr-2">
          <Checkbox
            checked={allSelected}
            data-state={someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked'}
            onCheckedChange={toggleAll}
            data-testid="lost-select-all-checkbox"
          />
          <span className="text-sm text-[#3C3C3C]">
            {selectedIds.size > 0
              ? `${selectedIds.size} selezionati`
              : `Seleziona tutti (${lostLeads.length})`}
          </span>
        </div>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          disabled={exporting || !canManage}
          data-testid="export-lost-csv-btn"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-1.5" />
          )}
          Esporta CSV
        </Button>

        {canManage && (
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => setShowConfirmModal(true)}
            disabled={selectedIds.size === 0 || recovering}
            data-testid="bulk-recover-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Recupera selezionati
          </Button>
        )}
      </div>

      {/* Tabella lead persi */}
      <div className="bg-white rounded-xl border border-[#EDEDED] overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[#737373] text-xs uppercase tracking-wide">
            <tr>
              <th className="p-3 w-10"></th>
              <th className="p-3 text-left">Cliente</th>
              <th className="p-3 text-left">Contatti</th>
              <th className="p-3 text-left">Motivo perdita</th>
              <th className="p-3 text-left">Perso il</th>
              <th className="p-3 text-left">Da</th>
            </tr>
          </thead>
          <tbody>
            {lostLeads.map((lead) => {
              const isSel = selectedIds.has(lead.id);
              return (
                <tr
                  key={lead.id}
                  className={`border-t border-[#EDEDED] hover:bg-[#FAFAFA] ${isSel ? 'bg-emerald-50/60' : ''}`}
                  data-testid={`lost-lead-row-${lead.id}`}
                >
                  <td className="p-3">
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggleOne(lead.id)}
                      data-testid={`lost-lead-checkbox-${lead.id}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-[#3C3C3C]">
                      {lead.first_name} {lead.last_name}
                    </div>
                    {lead.business_name && (
                      <div className="text-xs text-[#737373]">{lead.business_name}</div>
                    )}
                  </td>
                  <td className="p-3 text-[#737373] text-xs">
                    <div>{lead.phone || '—'}</div>
                    {lead.email && <div className="truncate max-w-[180px]">{lead.email}</div>}
                  </td>
                  <td className="p-3">
                    {lead.motivo_perdita ? (
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200">
                          <X className="w-3 h-3" />
                          {lead.motivo_perdita_label || lead.motivo_perdita}
                        </span>
                        {lead.motivo_perdita_note && (
                          <p className="text-xs text-[#737373] mt-1 line-clamp-2">
                            {lead.motivo_perdita_note}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-[#999]">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-[#737373]">
                    {lead.perso_at ? formatDate(lead.perso_at) : '—'}
                  </td>
                  <td className="p-3 text-xs text-[#737373]">
                    {lead.perso_by_user_name || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modale conferma recupero in blocco */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md" data-testid="bulk-recover-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <RotateCcw className="w-5 h-5" />
              Conferma recupero in blocco
            </DialogTitle>
            <DialogDescription>
              Stai per riportare <strong>{selectedIds.size}</strong> lead
              {selectedIds.size === 1 ? '' : ''} dallo stato "Perso" a "Nuovo".
              Preventivi, note e assegnazioni restano intatti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="bulk-recover-note" className="text-sm">
              Nota di recupero (opzionale)
            </Label>
            <Textarea
              id="bulk-recover-note"
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              placeholder="Es. Cliente ha richiamato dopo campagna di follow-up..."
              rows={3}
              data-testid="bulk-recover-note-input"
            />
            <p className="text-xs text-[#737373]">
              Sarà registrata come transizione e nota per ciascun lead.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={recovering}
              data-testid="bulk-recover-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleBulkRecover}
              disabled={recovering}
              data-testid="bulk-recover-confirm-btn"
            >
              {recovering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Recupero...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Conferma ({selectedIds.size})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LostLeadsPanel;
